import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IconButton } from '../../../../components/common/buttons/IconButton';
import { Icon } from '../../../../components/common/icon/Icon';
import { BasicInputWithExtras } from '../../../../components/common/inputs/BasicInputWithExtras';
import { SurveyForm } from '../../../../components/common/survey/form/SurveyForm';
import { sellTractorSurveySteps } from '../../../../constants/survey/sellTractorSurvey';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store/useStore';
import { InputValue, SurveyFormOptionDetail } from '../../../../interfaces/Element.interfaces';
import { userSellTractorSurveySlice } from '../../../../store/userSellTractorSurveySlice';
import { SellTractorSurveyPageWrapper } from '../SellTractorSurveyPageWrapper';

export const SellTractorSurveyTractorExtraInfo1Page = () => {
  const navigate = useNavigate();

  const defaultPlaceholder = '입력하기';
  const replacedPartIdPrefix = 'replaced-';
  const needRepairPartIdPrefix = 'need-repair-';
  const INITIAL_REPLACED_PARTS: SurveyFormOptionDetail[] = [
    { id: `${replacedPartIdPrefix}0`, value: '', placeholder: '예) 유압실린더' },
  ];
  const INITIAL_NEED_REPAIR_PARRTS: SurveyFormOptionDetail[] = [
    { id: `${needRepairPartIdPrefix}0`, value: '', placeholder: '예) PTO 고장' },
  ];
  const INITIAL_NEXT_ID = 1;

  const [replacedParts, setReplacedParts] = useState<SurveyFormOptionDetail[]>(INITIAL_REPLACED_PARTS);
  const [needRepairParts, setNeedRepairParts] = useState<SurveyFormOptionDetail[]>(INITIAL_NEED_REPAIR_PARRTS);
  const [nextReplacedPartId, setNextReplacedPartId] = useState(INITIAL_NEXT_ID);
  const [nextNeedRepairPartId, setNextNeedRepairPartId] = useState(INITIAL_NEXT_ID);

  const dispatch = useAppDispatch();
  const userSellTractorSurveySelector = useAppSelector((state) => state.userSellTractorSurvey);
  const userSellTractorSurveyActions = userSellTractorSurveySlice.actions;

  const currentStep = sellTractorSurveySteps.STEP4;
  const steps = Object.keys(sellTractorSurveySteps);

  const getNextnextOptions = (formOption: SurveyFormOptionDetail[], nextId: string) => {
    const nextOptions = formOption.concat({
      id: nextId,
      value: '',
      placeholder: defaultPlaceholder,
    });
    return nextOptions;
  };

  const handleAddReplacedPartInput = () => {
    if (replacedParts.length >= 10) {
      return;
    }

    const uniqueId = `${replacedPartIdPrefix}${nextReplacedPartId}`;
    const nextOptions = getNextnextOptions(replacedParts, uniqueId);

    setReplacedParts(nextOptions);
    setNextReplacedPartId(nextReplacedPartId + 1);
  };

  const handleAddNeedRepairPartInput = () => {
    if (needRepairParts.length >= 10) {
      return;
    }

    const uniqueId = `${needRepairPartIdPrefix}${nextNeedRepairPartId}`;
    const nextOptions = getNextnextOptions(needRepairParts, uniqueId);

    setNeedRepairParts(nextOptions);
    setNextNeedRepairPartId(nextNeedRepairPartId + 1);
  };

  const handleRemoveInput = (id: string, setParts: Dispatch<SetStateAction<SurveyFormOptionDetail[]>>) => {
    setParts((prevParts) => prevParts.filter((value) => value.id !== id));
  };

  const handleReplacedPartInputChange = (id: string, value: string) => {
    setReplacedParts((prevParts) => {
      return prevParts.map((part) => (part.id === id ? { ...part, value } : part));
    });
  };

  const handleNeedRepairPartInputChange = (id: string, value: string) => {
    setNeedRepairParts((prevParts) => {
      return prevParts.map((part) => (part.id === id ? { ...part, value } : part));
    });
  };

  useEffect(() => {
    const { replacedParts: savedReplacedParts, needRepairParts: savedNeedRepairParts } = userSellTractorSurveySelector;

    if (savedReplacedParts && savedReplacedParts.length > 0) {
      let replacedPartIdSuffix = nextReplacedPartId;
      const currentReplacedParts = savedReplacedParts.map((part: string, index) => {
        switch (index) {
          case 0:
            return {
              ...INITIAL_REPLACED_PARTS[0],
              value: part,
            };
          default: {
            const replacedUniqueId = `${replacedPartIdPrefix}${replacedPartIdSuffix}`;
            const convertedReplacedPart = { id: replacedUniqueId, value: part, placeholder: defaultPlaceholder };
            replacedPartIdSuffix += 1;
            return convertedReplacedPart;
          }
        }
      });
      setReplacedParts(currentReplacedParts);
      setNextNeedRepairPartId(replacedPartIdSuffix);
    }

    if (savedNeedRepairParts && savedNeedRepairParts.length > 0) {
      let needRepairPartIdSuffix = nextNeedRepairPartId;

      const currentNeedRepairParts = savedNeedRepairParts.map((part: string, index) => {
        switch (index) {
          case 0:
            return {
              ...INITIAL_NEED_REPAIR_PARRTS[0],
              value: part,
            };
          default: {
            const needRepairUniqueId = `${needRepairPartIdPrefix}${needRepairPartIdSuffix}`;
            const convertedNeedRepairPart = { id: needRepairUniqueId, value: part, placeholder: defaultPlaceholder };
            needRepairPartIdSuffix += 1;
            return convertedNeedRepairPart;
          }
        }
      });
      setNeedRepairParts(currentNeedRepairParts);
      setNextNeedRepairPartId(needRepairPartIdSuffix);
    }
  }, [userSellTractorSurveySelector.option]);

  return (
    <SellTractorSurveyPageWrapper
      headerTitle={'교체한 부품이나\n수리가 필요한 내용을 알려 주세요'}
      canConfirm
      confirmHandler={() => {
        const convertedReplacedParts = replacedParts
          .filter((part) => !!part.value)
          .map((filteredReplacedPart) => filteredReplacedPart.value);
        if (convertedReplacedParts && convertedReplacedParts.length > 0) {
          dispatch(userSellTractorSurveyActions.changeReplacedParts(convertedReplacedParts));
        }

        const convertedNeedRepairParts = needRepairParts
          .filter((part) => !!part.value)
          .map((filteredNeedRepairPart) => filteredNeedRepairPart.value);
        if (convertedNeedRepairParts && convertedNeedRepairParts.length > 0) {
          dispatch(userSellTractorSurveyActions.changeNeedRepairParts(convertedNeedRepairParts));
        }

        navigate('/survey/progress/sell-tractor/tire-condition', { replace: true });
      }}
      prevButtonHandler={() => navigate('/survey/progress/sell-tractor/working-machine', { replace: true })}
      steps={steps}
      currentStep={currentStep}
    >
      <SurveyForm padding={'0px 16px 24px 16px'}>
        <>
          <SurveyInputSection>
            {replacedParts.map((part) => {
              if (part.id === `${replacedPartIdPrefix}0`) {
                return (
                  <BasicInputWithExtras
                    key={part.id}
                    inputLabel={'교체한 부품 내역이 있나요?'}
                    isOptional
                    inputValue={part.value}
                    placeholder={part.placeholder}
                    onChange={(value: InputValue) => handleReplacedPartInputChange(part.id, value as string)}
                  />
                );
              }
              return (
                <BasicInputWithExtras
                  key={part.id}
                  inputValue={part.value}
                  placeholder={part.placeholder}
                  onChange={(value: InputValue) => handleReplacedPartInputChange(part.id, value as string)}
                >
                  <IconButton
                    onClickEvent={() => handleRemoveInput(part.id, setReplacedParts)}
                    icon={{ name: 'MinusCircle', width: 20, height: 20 }}
                  />
                </BasicInputWithExtras>
              );
            })}
          </SurveyInputSection>
          <AddItemButton onClick={handleAddReplacedPartInput} disabled={replacedParts.length >= 10}>
            <Icon name="Plus" width={14} height={14} />
            <AddItemButtonText>{'항목 추가'}</AddItemButtonText>
          </AddItemButton>
        </>
        <>
          <SurveyInputSection>
            {needRepairParts.map((part) => {
              if (part.id === `${needRepairPartIdPrefix}0`) {
                return (
                  <BasicInputWithExtras
                    key={part.id}
                    inputLabel={'수리가 필요한 항목이 있나요?'}
                    isOptional
                    inputValue={part.value}
                    placeholder={part.placeholder}
                    onChange={(value: InputValue) => handleNeedRepairPartInputChange(part.id, value as string)}
                  />
                );
              }
              return (
                <BasicInputWithExtras
                  key={part.id}
                  inputValue={part.value}
                  placeholder={part.placeholder}
                  onChange={(value: InputValue) => handleNeedRepairPartInputChange(part.id, value as string)}
                >
                  <IconButton
                    onClickEvent={() => handleRemoveInput(part.id, setNeedRepairParts)}
                    icon={{ name: 'MinusCircle', width: 20, height: 20 }}
                  />
                </BasicInputWithExtras>
              );
            })}
          </SurveyInputSection>
          <AddItemButton onClick={handleAddNeedRepairPartInput} disabled={needRepairParts.length >= 10}>
            <Icon name="Plus" width={14} height={14} />
            <AddItemButtonText>{'항목 추가'}</AddItemButtonText>
          </AddItemButton>
        </>
      </SurveyForm>
    </SellTractorSurveyPageWrapper>
  );
};

const SurveyInputSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--radius-large, 16px);
  align-self: stretch;
`;

const AddItemButton = styled.button`
  display: flex;
  max-height: 40px;
  padding: 16px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
  &:disabled {
    opacity: 0.3;
  }
`;

const AddItemButtonText = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;
